import React, { useMemo, useState, useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Paper,
    Radio,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import useDuplicidadesModule from './useDuplicidadesModule';
import { destinosEquivalentes } from './mapeamentoVinculos';

const LABELS = {
    DESCRICAO_NORMALIZADA: 'Descrição equivalente',
    CODIGO_COMPARTILHADO: 'Código compartilhado',
    FORNECEDOR_CPROD: 'Código do fornecedor',
    VINCULO_REPETIDO: 'Vínculo de embalagem repetido',
};
const valor = (v) => (v == null ? '—' : typeof v === 'boolean' ? (v ? 'Sim' : 'Não') : String(v));
const nomeVinculo = (v) =>
    `#${v.id} · ${v.codigoBarras} · ${v.Embalagem?.descricao || 'Embalagem indisponível'} · fator ${v.Embalagem?.fator ?? '—'}`;
function Mensagens({ items = [], severity = 'warning' }) {
    return (
        <Stack spacing={1}>
            {items.map((i, index) => (
                <Alert severity={severity} key={`${i.codigo}-${index}`}>
                    {i.mensagem}
                </Alert>
            ))}
        </Stack>
    );
}
function Grade({ columns, rows }) {
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {columns.map(([k, label]) => (
                            <TableCell key={k}>{label}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={`${r.tabela || ''}:${r.id ?? i}:${i}`}>
                            {columns.map(([k]) => (
                                <TableCell key={k}>{valor(r[k])}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
export default function DuplicidadesProdutos({ empresaInicial, selecaoInicial, onConcluida, onFechar, onGravando } = {}) {
    const selecionados = Array.isArray(selecaoInicial);
    const m = useDuplicidadesModule({ empresaInicial, selecaoInicial, onConcluida });
    useEffect(() => {
        onGravando?.(m.gravando);
    }, [m.gravando, onGravando]);
    const navigate = useNavigate();
    const [idsManuais, setIdsManuais] = useState('');
    const [erroManual, setErroManual] = useState('');
    const [confirmar, setConfirmar] = useState(false);
    const [cienteCusto, setCienteCusto] = useState(false);
    const [produtoHistorico, setProdutoHistorico] = useState('');
    const columns = useMemo(
        () => [
            { accessorKey: 'criterio', header: 'Motivo', Cell: ({ cell }) => LABELS[cell.getValue()] || cell.getValue() },
            {
                id: 'produtos',
                header: 'Cadastros envolvidos',
                accessorFn: (row) => row.produtos.map((p) => `#${p.id} — ${p.descricao}`).join(' | '),
            },
            {
                accessorKey: 'tipo',
                header: 'Diagnóstico',
                Cell: ({ cell }) =>
                    cell.getValue() === 'CONFLITO_CODIGO' ? (
                        <Chip size="small" color="warning" label="Código em cadastros distintos" />
                    ) : cell.getValue() === 'VINCULOS_REPETIDOS' ? (
                        'Repetição no mesmo produto'
                    ) : (
                        'Possível duplicidade'
                    ),
            },
        ],
        []
    );
    const manual = () => {
        const tokens = idsManuais.split(/[\s,;]+/).filter(Boolean);
        if (
            tokens.length < 2 ||
            tokens.length > 20 ||
            tokens.some((v) => !/^\d+$/.test(v) || !Number.isSafeInteger(Number(v)) || Number(v) < 1) ||
            new Set(tokens.map(Number)).size !== tokens.length
        ) {
            setErroManual('Informe de 2 a 20 IDs distintos, separados por vírgula.');
            return;
        }
        setErroManual('');
        m.comparar(tokens.map(Number));
    };
    const c = m.comparacao;
    const exibicao = m.previa || c;
    const vinculos = c?.apresentacoes || [];
    const preservados = vinculos.filter(
        (v) =>
            (Number(v.produtoId) === Number(m.principalId) || m.mapas[v.id]?.acao === 'PRESERVAR_VINCULO') &&
            !v.deletedAt &&
            (v.ativo === true || Number(v.ativo) === 1)
    );
    return (
        <Box component="fieldset" disabled={m.gravando} sx={{ border: 0, minWidth: 0, m: 0, p: { xs: 1, md: 2 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">{selecionados ? 'Mesclar produtos selecionados' : 'Revisão de duplicidades'}</Typography>
                <Button
                    disabled={m.gravando}
                    variant="contained"
                    color="secondary"
                    onClick={() => (onFechar ? onFechar() : navigate(-1))}
                >
                    Voltar
                </Button>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
                Identifique e compare os cadastros antes de decidir. Gerar a prévia não altera dados. A confirmação, quando
                autorizada e habilitada neste ambiente, aplica a mesclagem em uma transação auditada.
            </Alert>
            {!selecionados && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            A revisão utiliza a empresa do seu acesso atual.
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                fullWidth
                                label="Pesquisar descrição, código ou ID nos grupos encontrados"
                                value={m.pesquisa}
                                onChange={(e) => m.setPesquisa(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !m.ocupado) m.buscar();
                                }}
                            />
                            <Button
                                variant="contained"
                                disabled={m.ocupado || !m.empresaId || !m.criterios.length}
                                onClick={() => m.buscar()}
                            >
                                Identificar
                            </Button>
                        </Stack>
                        <Stack direction="row" flexWrap="wrap">
                            {Object.entries(LABELS).map(([k, label]) => (
                                <FormControlLabel
                                    key={k}
                                    label={label}
                                    control={
                                        <Checkbox
                                            checked={m.criterios.includes(k)}
                                            onChange={(e) =>
                                                m.setCriterios(
                                                    e.target.checked ? [...m.criterios, k] : m.criterios.filter((v) => v !== k)
                                                )
                                            }
                                        />
                                    }
                                />
                            ))}
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                fullWidth
                                label="Comparar cadastros conhecidos — IDs separados por vírgula"
                                value={idsManuais}
                                onChange={(e) => setIdsManuais(e.target.value)}
                                helperText="Use os IDs exibidos na listagem, não os códigos de barras."
                                error={!!erroManual}
                            />
                            <Button variant="outlined" onClick={manual} disabled={m.ocupado || !m.empresaId}>
                                Comparar seleção
                            </Button>
                        </Stack>
                        {erroManual && <Alert severity="warning">{erroManual}</Alert>}
                    </Stack>
                </Paper>
            )}
            {!selecionados && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                            label="Consultar mesclagem pelo ID do produto"
                            value={produtoHistorico}
                            onChange={(e) => setProdutoHistorico(e.target.value)}
                            inputProps={{ inputMode: 'numeric' }}
                        />
                        <Button
                            disabled={m.ocupado || !/^\d+$/.test(produtoHistorico) || Number(produtoHistorico) < 1}
                            onClick={() => m.consultarAuditoria({ produtoId: Number(produtoHistorico) })}
                        >
                            Consultar histórico
                        </Button>
                    </Stack>
                </Paper>
            )}
            {m.resultado && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Mesclagem {m.resultado.repetida ? 'já concluída anteriormente' : 'concluída'}: principal #
                    {m.resultado.principalId}. Auditoria {m.resultado.auditoriaId}.
                    <Button onClick={() => m.consultarAuditoria({ auditoriaId: m.resultado.auditoriaId })}>
                        Consultar auditoria
                    </Button>
                </Alert>
            )}
            {m.auditoria && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    {m.auditoria.semMesclagem ? (
                        <Typography>Produto sem mesclagem registrada.</Typography>
                    ) : (
                        <>
                            <Typography variant="h6">Auditoria {m.auditoria.id}</Typography>
                            <Typography>
                                Principal #{m.auditoria.resultado.principalId} · Origens:{' '}
                                {m.auditoria.resultado.origemIds.join(', ')} · Operador #{m.auditoria.operadorId}
                            </Typography>
                            <Typography>{m.auditoria.justificativa}</Typography>
                            <Typography variant="body2">
                                Origens permanecem consultáveis, inativas e bloqueadas para edição ou novos usos.
                            </Typography>
                            {!!m.auditoria.associacoesVinculos?.length && (
                                <Grade
                                    columns={[
                                        ['produtoEmbalagemOrigemId', 'Vínculo antigo inativo'],
                                        ['produtoEmbalagemDestinoId', 'Vínculo utilizado'],
                                    ]}
                                    rows={m.auditoria.associacoesVinculos}
                                />
                            )}
                            <Grade
                                columns={[
                                    ['tabela', 'Tabela'],
                                    ['id', 'Registro'],
                                    ['produtoIdAntes', 'Produto anterior'],
                                    ['produtoIdDepois', 'Produto após a ação'],
                                ]}
                                rows={m.auditoria.alteracoes}
                            />
                        </>
                    )}
                </Paper>
            )}
            {m.erro && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {m.erro}
                </Alert>
            )}
            {m.ocupado && (
                <Stack direction="row" spacing={2} sx={{ my: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>
                        {m.gravando ? 'Aplicando mesclagem e conferindo o histórico…' : 'Analisando cadastros e referências…'}
                    </Typography>
                </Stack>
            )}
            {m.lista && (
                <Box sx={{ mb: 3 }}>
                    <Mensagens items={m.lista.avisos} severity="info" />
                    <MaterialReactTable
                        columns={columns}
                        data={m.lista.data || []}
                        getRowId={(row) => row.id}
                        enableColumnFilters={false}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        manualPagination
                        rowCount={m.lista.meta.totalRowCount}
                        state={{ pagination: m.pagination, isLoading: m.ocupado }}
                        onPaginationChange={(updater) => {
                            const p = typeof updater === 'function' ? updater(m.pagination) : updater;
                            m.buscar(p);
                        }}
                        enableRowActions
                        renderRowActions={({ row }) => (
                            <Button
                                disabled={m.ocupado || row.original.produtos.length < 2 || row.original.produtos.length > 20}
                                onClick={() => m.comparar(row.original.produtos.map((p) => Number(p.id)))}
                                title="Grupos acima de 20 devem ser divididos pela seleção de IDs"
                            >
                                Comparar
                            </Button>
                        )}
                    />
                </Box>
            )}
            {c && (
                <Stack spacing={2}>
                    <Typography variant="h6">Comparação — empresa do cadastro: {c.escopo.empresaProprietariaId}</Typography>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1">1. Escolha o produto principal</Typography>
                        {c.produtos.map((p) => (
                            <Stack key={p.id} direction="row" alignItems="center">
                                <Radio
                                    checked={Number(m.principalId) === Number(p.id)}
                                    onChange={() => m.escolherPrincipal(p.id)}
                                    inputProps={{ 'aria-label': `Escolher ${p.descricao} como principal` }}
                                />
                                <Typography>
                                    #{p.id} — {p.descricao} · código {p.codigo} · {Number(p.ativo) === 1 ? 'Ativo' : 'Inativo'}
                                </Typography>
                            </Stack>
                        ))}
                        <Grade
                            columns={c.produtos
                                .map((p) => [String(p.id), `#${p.id}`])
                                .reduce((a, v) => [...a, v], [['campo', 'Campo divergente']])}
                            rows={c.divergencias.map((d) => ({
                                campo: d.campo,
                                ...Object.fromEntries(d.valores.map((v) => [String(v.produtoId), v.valor])),
                            }))}
                        />
                    </Paper>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1">2. Embalagens e códigos</Typography>
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Revise o tratamento sugerido. Um código novo será transferido ao principal com seu vínculo existente.
                            Um código equivalente utilizará o vínculo escolhido; o antigo ficará inativo e rastreável.
                            Não é necessário criar outra embalagem para apresentações já cadastradas.
                        </Alert>
                        {!m.principalId && <Alert severity="info">Escolha o principal para definir o mapeamento.</Alert>}
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            {vinculos.map((v) => (
                                <Box key={v.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
                                    <Typography>
                                        {nomeVinculo(v)} · produto #{v.produtoId}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Unidade: {v.Embalagem?.unidadeDescricao || 'Não verificada'} ·{' '}
                                        {v.deletedAt ? 'Excluído logicamente' : Number(v.ativo) === 1 ? 'Ativo' : 'Inativo'}
                                    </Typography>
                                    {c.equivalenciasVinculos?.find((e) => Number(e.produtoEmbalagemOrigemId) === Number(v.id))?.destinosLimitados && (
                                        <Alert severity="warning">Este código excede o limite de 100 vínculos para seleção de equivalentes e exige revisão cadastral.</Alert>
                                    )}
                                    {m.mapas[v.id]?.acao === 'ASSOCIAR_EQUIVALENTE' && (
                                        <Typography variant="body2" color="text.secondary">
                                            O vínculo #{v.id} ficará inativo na origem. Seu ID e UUID serão preservados na associação com #{m.mapas[v.id].produtoEmbalagemDestinoId}.
                                        </Typography>
                                    )}
                                    {m.principalId && Number(v.produtoId) !== Number(m.principalId) && (
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Tratamento proposto"
                                            sx={{ mt: 1 }}
                                            value={
                                                m.mapas[v.id]?.acao === 'ASSOCIAR_EQUIVALENTE'
                                                    ? String(m.mapas[v.id].produtoEmbalagemDestinoId)
                                                    : 'preservar'
                                            }
                                            onChange={(e) => m.alterarMapa(v.id, e.target.value)}
                                        >
                                            <MenuItem value="preservar">Transferir este vínculo e código ao principal</MenuItem>
                                            {destinosEquivalentes(c, v.id, m.principalId, m.mapas).map((destino) => (
                                                <MenuItem key={destino.id} value={String(destino.id)}>
                                                    Utilizar vínculo equivalente: {nomeVinculo(destino)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                        <TextField
                            select
                            fullWidth
                            label="Apresentação principal resultante"
                            value={m.principalEmbalagemId}
                            onChange={(e) => m.escolherEmbalagem(e.target.value)}
                            disabled={!m.principalId || m.gravando}
                            sx={{ mt: 2 }}
                        >
                            <MenuItem value="">Selecione</MenuItem>
                            {preservados.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                    {nomeVinculo(v)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Paper>
                    {exibicao.conferencias?.baseEstoque && (
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle1">Conversões das apresentações</Typography>
                            <Alert severity={exibicao.conferencias.baseEstoque.validada || exibicao.conferencias.baseEstoque.propostaCompativel ? 'info' : 'warning'} sx={{ my: 1 }}>
                                {exibicao.conferencias.baseEstoque.mensagem}
                            </Alert>
                            <Grade
                                columns={[
                                    ['descricao', 'Apresentação'],
                                    ['fator', 'Quantidade na base por apresentação'],
                                    ['unidadeBase', 'Unidade-base'],
                                ]}
                                rows={exibicao.conferencias.baseEstoque.conversoes || []}
                            />
                        </Paper>
                    )}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1">Preços e fornecedores</Typography>
                        <Grade
                            columns={[
                                ['id', 'Preço ID'],
                                ['produtoId', 'Produto'],
                                ['tabelaPrecoId', 'Tabela'],
                                ['embalagemId', 'Embalagem'],
                                ['preco', 'Preço'],
                                ['vigenciaInicio', 'Início'],
                                ['vigenciaFim', 'Fim'],
                                ['deletedAt', 'Exclusão'],
                            ]}
                            rows={c.precos}
                        />
                        <Grade
                            columns={[
                                ['id', 'Vínculo ID'],
                                ['produtoId', 'Produto'],
                                ['fornecedorId', 'Fornecedor'],
                                ['cProd', 'Código do fornecedor'],
                                ['xProd', 'Descrição observada'],
                                ['deletedAt', 'Exclusão'],
                            ]}
                            rows={c.fornecedores}
                        />
                        {!c.fornecedores.length && (
                            <Typography variant="body2">Nenhum vínculo de fornecedor visível nesta análise.</Typography>
                        )}
                    </Paper>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1">Referências encontradas</Typography>
                        <Grade
                            columns={[
                                ['tabela', 'Tabela'],
                                ['campo', 'Campo'],
                                ['cobertura', 'Verificação'],
                                ['ativos', 'Ativos'],
                                ['inativos', 'Inativos'],
                                ['excluidos', 'Excluídos'],
                            ]}
                            rows={exibicao.dependencias.map((d) => ({
                                ...d,
                                ativos: d.contagens?.ativos,
                                inativos: d.contagens?.inativos,
                                excluidos: d.contagens?.excluidos,
                            }))}
                        />
                    </Paper>
                    <TextField
                        label="Justificativa da mesclagem proposta"
                        multiline
                        minRows={2}
                        value={m.justificativa}
                        onChange={(e) => m.setJustificativa(e.target.value)}
                        inputProps={{ maxLength: 2000 }}
                    />
                    <Button
                        variant="contained"
                        disabled={m.ocupado || !m.principalId || !m.justificativa.trim()}
                        onClick={m.gerarPrevia}
                    >
                        Gerar prévia
                    </Button>
                    <Mensagens items={exibicao.avisos} severity="info" />
                    <Mensagens items={exibicao.conflitos} />
                    <Mensagens items={exibicao.impedimentos} severity="warning" />
                    {m.previa && (
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6">Prévia gerada — nenhuma alteração aplicada</Typography>
                            <Typography variant="body2">
                                Origens ficarão mescladas/inativas; outra mercadoria terá novo ID. A confirmação só aparece quando
                                a prévia estiver apta e a execução estiver habilitada neste ambiente.
                            </Typography>
                            <Grade
                                columns={[
                                    ['tabela', 'Entidade'],
                                    ['id', 'Registro'],
                                    ['acao', 'Ação proposta'],
                                    ['produtoIdDepois', 'Produto após a ação'],
                                    ['destinoEquivalenteId', 'Vínculo utilizado'],
                                    ['destinoId', 'Principal'],
                                    ['orientacao', 'Tratamento'],
                                ]}
                                rows={m.previa.plano}
                            />
                            <Typography variant="subtitle1" sx={{ mt: 2 }}>
                                Tratamento das referências
                            </Typography>
                            <Grade
                                columns={[
                                    ['tabela', 'Tabela'],
                                    ['campo', 'Referência'],
                                    ['registrosOrigem', 'Registros nas origens'],
                                    ['acao', 'Tratamento proposto'],
                                    ['cobertura', 'Cobertura'],
                                ]}
                                rows={m.previa.planoReferencias || []}
                            />
                            <Typography variant="subtitle1" sx={{ mt: 2 }}>
                                Conferência de estoque do conjunto
                            </Typography>
                            <Grade
                                columns={[
                                    ['empresaId', 'Empresa'],
                                    ['almoxarifadoId', 'Almoxarifado'],
                                    ['entradas', 'Entradas'],
                                    ['saidas', 'Saídas'],
                                    ['bloqueios', 'Bloqueios'],
                                    ['saldoFisico', 'Físico'],
                                    ['saldoDisponivel', 'Disponível'],
                                ]}
                                rows={m.previa.conferencias.estoque || []}
                            />
                            {m.previa.aptaParaMesclagem && (
                                <Button
                                    color="warning"
                                    variant="contained"
                                    sx={{ mt: 2 }}
                                    disabled={m.ocupado}
                                    onClick={() => {
                                        setCienteCusto(false);
                                        setConfirmar(true);
                                    }}
                                >
                                    Revisar confirmação da mesclagem
                                </Button>
                            )}
                            {!m.previa.aptaParaMesclagem && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    {m.previa.impedimentos?.some((i) => ['EXECUCAO_DESABILITADA', 'CONFIGURACAO_MESCLAGEM_INVALIDA'].includes(i.codigo))
                                        ? 'Mesclagem bloqueada pela configuração do servidor. Solicite revisão ao administrador; alterar as embalagens não resolve esse bloqueio.'
                                        : 'Mesclagem bloqueada. Resolva os impedimentos específicos apresentados acima.'}
                                    {' '}Depois gere uma nova prévia; a confirmação aparecerá quando todas as validações forem atendidas.
                                </Alert>
                            )}
                            <Alert severity="info" sx={{ mt: 2 }}>
                                As quantidades de estoque existentes e os valores históricos serão preservados.
                                Os fatores de conversão não serão reaplicados sobre os saldos.
                            </Alert>
                        </Paper>
                    )}
                </Stack>
            )}
            <Dialog
                open={confirmar && !!m.previa?.aptaParaMesclagem}
                onClose={() => {
                    if (!m.ocupado) setConfirmar(false);
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Confirmar mesclagem de produtos</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography>
                            Principal: #{m.previa?.principalId}. Origens: {m.previa?.origemIds?.join(', ')}.
                        </Typography>
                        <Typography>
                            As origens ficarão inativas, ligadas ao principal e bloqueadas para reutilização. Não há desfazimento
                            automático após a confirmação.
                        </Typography>
                        <Typography>
                            Confira também as associações de embalagens propostas e os fatores das apresentações.
                            Vínculos equivalentes serão inativados na origem, mantendo a associação auditada com o vínculo utilizado.
                        </Typography>
                        <FormControlLabel
                            control={<Checkbox checked={cienteCusto} onChange={(e) => setCienteCusto(e.target.checked)} />}
                            label="Conferi a identidade e as embalagens. Estou ciente de que a consolidação pode mudar o último custo consultado, preservando os valores históricos."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button disabled={m.ocupado} onClick={() => setConfirmar(false)}>
                        Voltar à prévia
                    </Button>
                    <Button
                        color="warning"
                        variant="contained"
                        disabled={m.ocupado || !cienteCusto}
                        onClick={() => {
                            setConfirmar(false);
                            m.confirmarMesclagem();
                        }}
                    >
                        Confirmar mesclagem
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
